import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axiosInstance from "../../../api/axiosInstance";
import endpoints from "../../../api/endpoints";
import store, { RootState } from "../../../redux/store";
import { jwtDecode } from "jwt-decode";
import axios from "axios";

interface AuthState {
  isLogged: boolean;
  loading: boolean;
  error: string | null;
  token: string | null;
  refreshToken: string | null;
  expiration: string | null;
  businessId: string | null;
  roles: string[];
  name: string | null;
}

const loadStateFromLocalStorage = (): AuthState => {
  try {
    const serializedState = localStorage.getItem("authState");
    if (serializedState === null) {
      return {
        isLogged: false,
        loading: false,
        error: null,
        token: null,
        refreshToken: null,
        expiration: null,
        businessId: null,
        roles: [],
        name: null,
      };
    }
    return JSON.parse(serializedState);
  } catch (err) {
    console.error("Could not load state", err);
    return {
      isLogged: false,
      loading: false,
      error: null,
      token: null,
      refreshToken: null,
      expiration: null,
      businessId: null,
      roles: [],
      name: null,
    };
  }
};

const saveStateToLocalStorage = (state: AuthState) => {
  try {
    const serializedState = JSON.stringify(state);
    localStorage.setItem("authState", serializedState);
  } catch (err) {
    console.error("Could not save state", err);
  }
};

const initialState: AuthState = loadStateFromLocalStorage();

interface AuthInput {
  username: string;
  password: string;
}

interface AuthResponseData {
  token: string;
  refreshToken: string;
  expiration: string;
}

interface ApiResponse {
  success: boolean;
  message: string;
  data: AuthResponseData;
}

interface DecodedToken {
  businessid: string;
  Roles: string[];
  Username: string;
  exp: number;
  jti: string;
}

const calculateExpirationDelay = (expiration: string): number => {
  const expirationDate = new Date(expiration);
  const currentTime = new Date();
  return expirationDate.getTime() - currentTime.getTime();
};

const scheduleTokenRefresh = (
  accessToken: string,
  refreshToken: string,
  expiration: string
) => {
  const delay = calculateExpirationDelay(expiration);

  setTimeout(() => {
    store.dispatch(
      refreshAuthToken({ AccessToken: accessToken, RefreshToken: refreshToken })
    );
  }, delay);
};

export const authenticateUser = createAsyncThunk(
  "auth/authenticateUser",
  async (data: AuthInput, { rejectWithValue }) => {
    try {
      const response = await axios.post<ApiResponse>(
        endpoints.login.authenticateUser,
        data
      );

      if (response.data.success) {
        const { token, refreshToken, expiration } = response.data.data;
        const decodedToken = jwtDecode<DecodedToken>(token);

        const authState: AuthState = {
          isLogged: true,
          loading: false,
          error: null,
          token,
          refreshToken,
          expiration,
          businessId: decodedToken.businessid,
          roles: decodedToken.Roles,
          name: decodedToken.Username,
        };

        saveStateToLocalStorage(authState);
        scheduleTokenRefresh(token, refreshToken, expiration);

        return authState;
      } else {
        return rejectWithValue(response.data.data);
      }
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.response?.data || error.message
          : "An unexpected error occurred"
      );
    }
  }
);

interface RefreshTokenInput {
  AccessToken: string;
  RefreshToken: string;
}

export const refreshAuthToken = createAsyncThunk(
  "auth/refreshAuthToken",
  async (data: RefreshTokenInput, { rejectWithValue, getState }) => {
    try {
      const response = await axios.post<ApiResponse>(
        endpoints.login.refreshToken,
        data
      );

      if (response.data.success) {
        const { token, expiration, refreshToken } = response.data.data;
        const decodedToken = jwtDecode<DecodedToken>(token);
        const state = getState() as RootState;

        const authState: AuthState = {
          ...state.authSlice,
          token,
          refreshToken,
          expiration,
          businessId: decodedToken.businessid,
          roles: decodedToken.Roles,
          name: decodedToken.Username,
        };

        saveStateToLocalStorage(authState);
        scheduleTokenRefresh(token, refreshToken, expiration);

        return authState;
      } else {
        return rejectWithValue(response.data.message);
      }
    } catch (error) {
      return rejectWithValue(
        axios.isAxiosError(error)
          ? error.response?.data || error.message
          : "An unexpected error occurred"
      );
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.isLogged = false;
      state.token = null;
      state.refreshToken = null;
      state.expiration = null;
      state.businessId = null;
      state.roles = [];
      state.name = null;
      saveStateToLocalStorage(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(authenticateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        authenticateUser.fulfilled,
        (state, action: PayloadAction<AuthState>) => {
          Object.assign(state, action.payload);
        }
      )
      .addCase(authenticateUser.rejected, (state, action) => {
        const payload = action.payload as any;

        state.loading = false;

        if (typeof payload === "string") {
          state.error = payload;
        } else if (payload?.detail) {
          state.error = payload.detail;
        } else if (payload?.title) {
          state.error = payload.title;
        } else {
          state.error = "Une erreur interne du serveur est survenue.";
        }
      })
      .addCase(
        refreshAuthToken.fulfilled,
        (state, action: PayloadAction<AuthState>) => {
          Object.assign(state, action.payload);
        }
      )
      .addCase(refreshAuthToken.rejected, (state, action) => {
        state.isLogged = false;
        state.token = null;
        state.refreshToken = null;
        state.expiration = null;
        state.businessId = null;
        state.roles = [];
        state.name = null;
        state.error =
          (action.payload as string) || "Échec du rafraîchissement du token";
        saveStateToLocalStorage(state);
      });
  },
});

export const { logout } = authSlice.actions;
export default authSlice.reducer;
